import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { PlusCircle, ChevronDown, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";

interface AddToHubButtonProps {
  resourceType: string;
  resourceId: string;
  title?: string;
  thumbnailUrl?: string;
  size?: "sm" | "default";
  variant?: "ghost" | "outline" | "default";
  className?: string;
}

interface Hub {
  id: string;
  name: string;
  role: string;
}

interface CheckResult {
  addedToHubs: Array<{ hubId: string; hubName: string }>;
}

export default function AddToHubButton({
  resourceType,
  resourceId,
  title,
  thumbnailUrl,
  size = "sm",
  variant = "ghost",
  className = "",
}: AddToHubButtonProps) {
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Fetch user's hubs
  const { data: hubs = [], isLoading: hubsLoading } = useQuery<Hub[]>({
    queryKey: ["/api/hubs/managed"],
    enabled: isAuthenticated && open,
  });

  // Check which hubs this resource is already in.
  // Fetched on mount (not just on open) so the grayed-out state is ready
  // immediately when the user clicks the button.
  const { data: checkData, isLoading: checkLoading } = useQuery<CheckResult>({
    queryKey: ["/api/hub-resources/check", resourceType, resourceId],
    enabled: isAuthenticated,
    queryFn: async () => {
      return apiRequest<CheckResult>(
        `/api/hub-resources/check?resourceType=${encodeURIComponent(resourceType)}&resourceId=${encodeURIComponent(resourceId)}`
      );
    },
    // Never mark stale — the mutation's invalidateQueries is the only refresh trigger.
    // This prevents a background refetch from briefly showing all hubs as selectable
    // before the fresh membership check arrives.
    staleTime: Infinity,
  });

  const addedHubIds = new Set((checkData?.addedToHubs ?? []).map((h) => h.hubId));

  const addMutation = useMutation({
    mutationFn: () => {
      if (resourceType === "timer") {
        return apiRequest("/api/resource-import", {
          method: "POST",
          data: {
            resourceType: "timer",
            resourceId,
            targetHubIds: Array.from(selected),
            title,
          },
        });
      }
      return apiRequest("/api/hub-resources", {
        method: "POST",
        data: {
          hubIds: Array.from(selected),
          resourceType,
          resourceId,
          title,
          thumbnailUrl,
        },
      });
    },
    onSuccess: (data: any) => {
      setOpen(false);
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ["/api/hub-resources/check", resourceType, resourceId] });
      queryClient.invalidateQueries({ queryKey: ["/api/hub-resources"] });
      const copiedTimerCount = resourceType === "timer" ? (data.copies?.length ?? 0) : 0;
      if (copiedTimerCount > 0) {
        queryClient.invalidateQueries({ queryKey: ["/api/timers"] });
      }
      toast({
        title: copiedTimerCount > 0
          ? copiedTimerCount === 1 ? "Timer copied!" : "Timers copied!"
          : data.added?.length > 0 ? "Added to hub!" : "Already in hub",
        description: copiedTimerCount > 0
          ? `Created ${copiedTimerCount} Timer ${copiedTimerCount === 1 ? "copy" : "copies"} in the selected Hubs.`
          : data.message,
      });
    },
    onError: () => {
      toast({ title: "Could not add to hub", variant: "destructive" });
    },
  });

  function toggleHub(hubId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(hubId)) {
        next.delete(hubId);
      } else {
        next.add(hubId);
      }
      return next;
    });
  }

  // Sort: already-added hubs float to the top so users immediately see what's assigned
  const visibleHubs = resourceType === "timer"
    ? hubs.filter((hub) => ["owner", "manager", "collaborator"].includes(hub.role))
    : hubs;
  const sortedHubs = [...visibleHubs].sort((a, b) => {
    const aAdded = addedHubIds.has(a.id) ? 0 : 1;
    const bAdded = addedHubIds.has(b.id) ? 0 : 1;
    return aAdded - bAdded;
  });

  const canAdd = selected.size > 0 && !addMutation.isPending;

  if (!isAuthenticated) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={`flex items-center gap-1.5 ${className}`}
          onClick={(e) => e.stopPropagation()}
        >
          <PlusCircle className="w-4 h-4" />
          <span className="font-medium">Add to Hub</span>
          <ChevronDown className="w-3 h-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start" onClick={(e) => e.stopPropagation()}>
        <div className="px-3 py-2.5 border-b border-gray-100">
          <p className="text-sm font-semibold text-gray-900">Add to Hub</p>
          <p className="text-xs text-muted-foreground mt-0.5">Select hubs to save this resource to</p>
        </div>

        <div className="max-h-56 overflow-y-auto py-1">
          {hubsLoading || checkLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          ) : visibleHubs.length === 0 ? (
            <p className="text-sm text-muted-foreground px-3 py-4 text-center">
              {resourceType === "timer" ? "No editable hubs found" : "No hubs found"}
            </p>
          ) : (
            sortedHubs.map((hub) => {
              const isAlreadyAdded = addedHubIds.has(hub.id);
              return isAlreadyAdded ? (
                // Already assigned — show as a checked, disabled checkbox so the
                // user can immediately see which hubs contain this resource.
                <div
                  key={hub.id}
                  className="flex items-center gap-2.5 px-3 py-2 bg-green-50/60"
                  title="Already saved to this hub"
                >
                  <Checkbox
                    checked
                    disabled
                    className="shrink-0 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                  />
                  <span className="text-sm text-gray-700 truncate">{hub.name}</span>
                  <span className="text-xs text-green-600 font-medium ml-auto shrink-0">Saved</span>
                </div>
              ) : (
                // Not yet assigned — interactive checkbox
                <label
                  key={hub.id}
                  className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <Checkbox
                    checked={selected.has(hub.id)}
                    onCheckedChange={() => toggleHub(hub.id)}
                    className="shrink-0"
                  />
                  <span className="text-sm text-gray-800 truncate">{hub.name}</span>
                </label>
              );
            })
          )}
        </div>

        <div className="px-3 py-2.5 border-t border-gray-100">
          <Button
            className="w-full"
            size="sm"
            disabled={!canAdd}
            onClick={() => addMutation.mutate()}
          >
            {addMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Adding…
              </>
            ) : (
              <>
                <PlusCircle className="w-4 h-4 mr-2" />
                Add to {selected.size > 0 ? `${selected.size} Hub${selected.size !== 1 ? "s" : ""}` : "Hub"}
              </>
            )}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
